import { useCallback, useEffect, useRef, useState } from "react";
import type { QuestionnaireSummaryListItem } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type QuestionnaireSummariesState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; summaries: QuestionnaireSummaryListItem[] };

export interface UseQuestionnaireSummariesResult {
  state: QuestionnaireSummariesState;
  refetch: () => void;
}

export function useQuestionnaireSummaries(): UseQuestionnaireSummariesResult {
  const api = useApi();
  const [state, setState] = useState<QuestionnaireSummariesState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchSummaries = useCallback(() => {
    api.questionnaireSummaries
      .list()
      .then((summaries) => {
        if (!cancelledRef.current) {
          setState({ status: "success", summaries });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({ status: "error", error: err instanceof Error ? err.message : "Failed to load summaries" });
        }
      });
  }, [api]);

  useEffect(() => {
    cancelledRef.current = false;
    fetchSummaries();
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchSummaries]);

  return { state, refetch: fetchSummaries };
}
